import { useEffect, useState } from "react";

const MONO = "'IBM Plex Mono', ui-monospace, monospace";
const DISPLAY = "'Bricolage Grotesque', 'Source Sans 3', system-ui, sans-serif";

export function saoPauloMonth(date = new Date()) {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Sao_Paulo",
    year: "numeric",
    month: "2-digit",
  }).formatToParts(date);
  const year = parts.find(part => part.type === "year")?.value;
  const month = parts.find(part => part.type === "month")?.value;
  return `${year}-${month}`;
}

function monthLabel(month) {
  if (!month) return "";
  const [year, numericMonth] = month.split("-").map(Number);
  return new Intl.DateTimeFormat("pt-BR", { month: "long", year: "numeric", timeZone: "UTC" })
    .format(new Date(Date.UTC(year, numericMonth - 1, 1)));
}

function dateLabel(date) {
  if (!date) return "—";
  const parsed = new Date(`${date.slice(0, 10)}T12:00:00Z`);
  return new Intl.DateTimeFormat("pt-BR", { day: "2-digit", month: "long", year: "numeric", timeZone: "UTC" }).format(parsed);
}

function displayPoints(points) {
  return points == null ? "Pendente de evidência" : `${points > 0 ? "+" : ""}${points} pts`;
}

function jsonFromResponse(response) {
  return response.json().catch(() => ({})).then(body => {
    if (!response.ok) throw new Error(body.error || body.message || "Não foi possível carregar a prévia.");
    return body;
  });
}

function NumberRow({ label, value, theme, emphasis = false, tone }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12, padding: "7px 0", borderBottom: `1px solid ${theme.nuLine || theme.sandDark}` }}>
      <span style={{ color: theme.muted, fontSize: 13 }}>{label}</span>
      <span style={{ color: tone || theme.text, fontFamily: MONO, fontSize: emphasis ? 15 : 13, fontWeight: emphasis ? 700 : 500, textAlign: "right" }}>{value}</span>
    </div>
  );
}

export default function NovemberPointsPreview({ theme, currentUser }) {
  const th = theme;
  const line = th.nuLine || th.sandDark;
  const isConsultant = currentUser?.role === "consultant";

  const [expanded, setExpanded] = useState(false);
  const [month, setMonth] = useState(() => saoPauloMonth());
  const [selectedId, setSelectedId] = useState("");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const choices = Array.isArray(data?.availableConsultants) ? data.availableConsultants : [];

  useEffect(() => {
    if (!expanded) return undefined;
    const controller = new AbortController();
    const params = new URLSearchParams({ mes: month });
    const requestedId = isConsultant ? currentUser?.id : selectedId;
    if (requestedId != null && requestedId !== "") params.set("consultantId", String(requestedId));
    setData(null);
    setError("");
    setLoading(true);
    fetch(`/api/pontos/novembro?${params.toString()}`, {
      credentials: "include",
      signal: controller.signal,
    })
      .then(jsonFromResponse)
      .then(result => {
        if (!controller.signal.aborted) setData(result);
      })
      .catch(err => {
        if (!controller.signal.aborted) setError(err.message || "Não foi possível carregar a prévia.");
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [expanded, month, selectedId, isConsultant, currentUser?.id, attempt]);

  const open = () => setExpanded(value => !value);
  const subtotal = data?.consultant?.knownSubtotal;
  const deductions = data?.consultant?.bindingDeductions;
  const total = data?.consultant?.total;
  const totalBeforePenalties = data?.consultant?.totalBeforeBindingPenalties;
  const components = data?.consultant?.components || {};
  const sales = components.sales || {};
  const componentRows = [
    ["Pós-venda", components.postSale?.points],
    ["Indicações", components.referrals?.points],
    ["Disciplina", components.discipline?.points],
    ["Prospecção", components.prospecting?.points],
  ];

  return (
    <section aria-label="Prévia de pontos de novembro" style={{ marginTop: 14, border: `1px solid ${line}`, borderLeft: "3px solid #7A5AF8", borderRadius: 4, background: th.sandDark || "#F7F4EC" }}>
      <button type="button" onClick={open} aria-expanded={expanded}
        style={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, padding: "11px 13px", border: 0, background: "transparent", color: th.text, cursor: "pointer", textAlign: "left", fontFamily: "inherit" }}>
        <span>
          <span style={{ display: "block", fontFamily: DISPLAY, fontWeight: 700, fontSize: 16 }}>Prévia de novembro</span>
          <span style={{ display: "block", marginTop: 2, fontSize: 12, color: th.muted }}>Projeção informativa · não são pontos concedidos</span>
        </span>
        <span aria-hidden="true" style={{ color: th.muted, fontSize: 12 }}>{expanded ? "Fechar" : "Abrir"}</span>
      </button>
      {expanded && (
        <div style={{ padding: "0 13px 14px", borderTop: `1px solid ${line}` }}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "end", padding: "12px 0 8px" }}>
            {!isConsultant && (
              <label style={{ display: "grid", gap: 4, flex: "1 1 190px", color: th.muted, fontSize: 12 }}>
                Consultor
                <select value={selectedId || (data?.consultant?.id != null ? String(data.consultant.id) : "")} onChange={event => setSelectedId(event.target.value)} style={{ minHeight: 36, padding: "6px 8px", border: `1px solid ${line}`, borderRadius: 3, background: th.background || "#fff", color: th.text, font: "inherit" }}>
                  <option value="">Selecione um consultor</option>
                  {choices.map(person => <option key={person.id} value={person.id}>{person.name || `Consultor ${person.id}`}</option>)}
                </select>
              </label>
            )}
            <label style={{ display: "grid", gap: 4, color: th.muted, fontSize: 12 }}>
              Mês de origem
              <input type="month" value={month} onChange={event => setMonth(event.target.value)} aria-label="Mês de origem" style={{ minHeight: 36, padding: "6px 8px", border: `1px solid ${line}`, borderRadius: 3, background: th.background || "#fff", color: th.text, font: "inherit" }} />
            </label>
          </div>
          <div style={{ fontSize: 12, color: th.muted, marginBottom: 10 }}>
            Regras com início em <strong style={{ color: th.text }}>{dateLabel(data?.policyStartsOn || "2026-11-01")}</strong>
            {data?.sourceMonth && <> · origem: {monthLabel(data.sourceMonth)}</>}
            {data?.asOf && <> · dados consultados em {dateLabel(data.asOf)}</>}
          </div>
          {loading && (
            <div role="status" aria-label="Carregando a projeção" style={{ padding: "10px 0" }}>
              {[0, 1, 2].map(row => (
                <div key={row} style={{ display: "flex", justifyContent: "space-between", gap: 16, padding: "9px 0", borderBottom: `1px solid ${line}` }}>
                  <span style={{ display: "block", width: row === 2 ? "42%" : "34%", height: 10, borderRadius: 2, background: "#E6E0D5" }} />
                  <span style={{ display: "block", width: "22%", height: 10, borderRadius: 2, background: "#E6E0D5" }} />
                </div>
              ))}
            </div>
          )}
          {error && (
            <div role="alert" style={{ padding: "10px 12px", border: "1px solid #E7B8AE", background: "#FFF5F1", color: "#8B3326", fontSize: 13 }}>
              <div>{error}</div>
              <button type="button" onClick={() => setAttempt(value => value + 1)} style={{ marginTop: 8, border: 0, background: "transparent", color: "#8B3326", fontWeight: 700, textDecoration: "underline", cursor: "pointer", font: "inherit" }}>Tentar novamente</button>
            </div>
          )}
          {data && (
            <div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 9 }}>
                <span style={{ padding: "5px 8px", background: "#F5F3FF", color: "#5B21B6", fontSize: 11, fontWeight: 700, letterSpacing: ".04em", textTransform: "uppercase" }}>Somente projeção</span>
                <span style={{ padding: "5px 8px", background: "#F4EBDD", color: th.text, fontSize: 11, fontWeight: 700 }}>Ainda não ativada</span>
              </div>
              <div style={{ fontFamily: DISPLAY, fontWeight: 700, fontSize: 16, color: th.text, marginBottom: 2 }}>{data.consultant?.name || "Consultor"}</div>
              <div style={{ fontSize: 13, color: th.muted, marginBottom: 9 }}>
                Meta mensal <strong style={{ color: th.text }}>{data.monthlyTarget ?? "—"} pts</strong>
                {" · "}Supermeta <strong style={{ color: th.text }}>{data.stretchTarget ?? "—"} pts</strong>
              </div>
              <div style={{ borderTop: `1px solid ${line}` }}>
                <NumberRow label="Vendas · base" value={displayPoints(sales.base)} theme={th} />
                <NumberRow label="Vendas · bônus por supermeta (+20%)" value={displayPoints(sales.bonus)} theme={th} />
                <NumberRow label="Vendas · pontos projetados" value={displayPoints(sales.points)} theme={th} />
                {componentRows.map(([label, points]) => <NumberRow key={label} label={label} value={displayPoints(points)} theme={th} />)}
                {totalBeforePenalties != null && <NumberRow label="Subtotal antes das penalidades vinculantes" value={`${totalBeforePenalties} pts`} theme={th} />}
                {deductions != null && <NumberRow label="Deduções vinculantes (aplicadas ao total)" value={`−${deductions} pts`} theme={th} tone={deductions > 0 ? "#9B3A2D" : undefined} />}
                <NumberRow label="Subtotal conhecido" value={subtotal == null ? "Pendente de evidência" : `${subtotal} pts`} theme={th} emphasis />
                <NumberRow label="Total projetado · piso zero" value={total == null ? "Total completo indisponível" : `${Math.max(0, total)} pts · não concedidos`} theme={th} emphasis tone="#5B21B6" />
              </div>
              {data.consultant?.pendingReasons?.length > 0 && (
                <div style={{ marginTop: 11, padding: "9px 11px", background: "#FBF3E5", borderLeft: "2px solid #C18B3C" }}>
                  <div style={{ color: th.text, fontSize: 12, fontWeight: 700, marginBottom: 4 }}>Evidências pendentes</div>
                  {data.consultant.pendingReasons.map((reason, index) => <div key={`${reason}-${index}`} style={{ color: th.muted, fontSize: 12, padding: "2px 0" }}>{reason}</div>)}
                </div>
              )}
              <div style={{ marginTop: 10, fontSize: 11, lineHeight: 1.45, color: th.muted }}>
                Esta simulação usa regras com vigência em novembro. Os pontos só serão oficiais após ativação e validação das evidências.
              </div>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
